import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AccountCircle,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  ContentCut as MasterIcon,
  Person as ClientIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  NotificationsActive as NotifIcon,
} from "@mui/icons-material";
import { ColorModeContext } from "../theme/AppThemeProvider";
import { enablePushNotifications } from "../lib/push";

interface HeaderProps {
  title: string;
}

const getApiBase = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) return "/api";
  if (
    envBase.startsWith("http://") &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:"
  ) {
    return envBase.replace(/^http:\/\//, "https://");
  }
  return envBase;
};

export default function Header({ title }: HeaderProps) {
  const API_BASE = getApiBase();
  const navigate = useNavigate();
  const { mode, setMode } = React.useContext(ColorModeContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [switchingRole, setSwitchingRole] = React.useState(false);
  const [editNameOpen, setEditNameOpen] = React.useState(false);
  const [editNameValue, setEditNameValue] = React.useState("");
  const [savingName, setSavingName] = React.useState(false);
  
  const userRole = localStorage.getItem("user_role") || "client";
  const userName = localStorage.getItem("user_name") || "Пользователь";

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEnablePush = async () => {
    handleClose();
    const res = await enablePushNotifications();
    alert(res.message);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_name");
    handleClose();
    window.location.replace("/login");
  };

  const logoutAndGoToLoginAs = (as: "admin" | "client") => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_name");
    handleClose();
    window.location.replace(`/login?as=${as}`);
  };

  const openEditName = () => {
    setEditNameValue(userName);
    setEditNameOpen(true);
  };

  const saveMyName = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      handleLogout();
      return;
    }

    const name = editNameValue.trim();
    if (!name) return;

    setSavingName(true);
    try {
      const resp = await fetch(`${API_BASE}/auth/me/name`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ full_name: name }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        alert(err?.detail || "Не удалось изменить имя");
        return;
      }

      localStorage.setItem("user_name", name);
      setEditNameOpen(false);
    } catch {
      alert("Не удалось изменить имя");
    } finally {
      setSavingName(false);
    }
  };

  const switchMeToClient = async () => {
    setSwitchingRole(true);
    try {
      alert("Чтобы войти как клиент, нужно выйти и войти заново как клиент.");
    } finally {
      setSwitchingRole(false);
      logoutAndGoToLoginAs("client");
    }
  };

  const getRoleIcon = () => {
    switch (userRole) {
      case "admin":
        return <AdminIcon />;
      case "master":
        return <MasterIcon />;
      default:
        return <ClientIcon />;
    }
  };

  const getRoleText = () => {
    switch (userRole) {
      case "admin":
        return "Администратор";
      case "master":
        return "Мастер";
      default:
        return "Клиент";
    }
  };

  const getRoleColor = () => {
    switch (userRole) {
      case "admin":
        return "error";
      case "master":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <>
      <AppBar position="static" sx={{ mb: 3 }}>
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            <Box
              component="img"
              src="/logo-white-192.png"
              alt="Зеркало"
              onClick={() => navigate("/")}
              sx={{
                width: 40,
                height: 40,
                objectFit: "contain",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, next) => {
                if (next === "light" || next === "dark") setMode(next);
              }}
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                borderRadius: 999,
                p: 0.25,
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: 999,
                  px: 1,
                  minWidth: 0,
                },
              }}
            >
              <ToggleButton value="light" aria-label="Светлая тема">
                <LightModeIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Тёмная тема">
                <DarkModeIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ textAlign: "right", mr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {getRoleText()}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "0.7rem", opacity: 0.8 }}>
                {userName}
              </Typography>
            </Box>
            
            <Avatar
              onClick={handleMenu}
              sx={{ 
                cursor: "pointer",
                bgcolor: `${getRoleColor()}.main`,
                width: 36,
                height: 36
              }}
            >
              {getRoleIcon()}
            </Avatar>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              onClick={handleClose}
            >
              {userRole === "client" && (
                <MenuItem onClick={openEditName}>
                  <ListItemIcon>
                    <AccountCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Изменить имя</ListItemText>
                </MenuItem>
              )}
              {userRole === "admin" && (
                <MenuItem onClick={() => void switchMeToClient()} disabled={switchingRole}>
                  <ListItemIcon>
                    <ClientIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Войти как клиент</ListItemText>
                </MenuItem>
              )}
              {userRole === "admin" && (
                <MenuItem onClick={() => logoutAndGoToLoginAs("admin")}>
                  <ListItemIcon>
                    <AdminIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Сменить аккаунт</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => void handleEnablePush()}>
                <ListItemIcon>
                  <NotifIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Включить уведомления</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Выйти</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog open={editNameOpen} onClose={() => setEditNameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Изменить имя</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Имя"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameOpen(false)} disabled={savingName}>
            Отмена
          </Button>
          <Button variant="contained" onClick={() => void saveMyName()} disabled={savingName || !editNameValue.trim()}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
